import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useInvoice } from '../context/InvoiceContext';

const Sidebar = ({ mobileOpen, onMobileClose, collapsed }) => {
  const { currentUser, logout, quizRunning, hasPermission } = useInvoice();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);
  const canManageInterviews = isAdmin || hasPermission('manage_interviews');
  const canManageLeads = isAdmin || hasPermission('manage_leads');
  // Base "User" role is locked to Todos/Timesheets/Calendar/Leave Policy. create_invoice
  // defaults to true for every employee, so it can't be used to selectively unlock
  // Invoices here — it stays hidden for restricted employees, no exceptions.
  const isRestrictedEmployee = currentUser?.role === 'employee';
  const canSeeInvoices = !isRestrictedEmployee;

  const bg          = '#FFFFFF';
  const border      = '#EAE7E4';
  const textPrimary = '#201E1C';
  const textMuted   = '#44403C';
  const activeColor = '#B91C1C';
  const activeBg    = 'rgba(185,28,28,0.06)';
  const hoverBg     = 'rgba(0,0,0,0.04)';

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const NavLink = ({ to, label, exact = false }) => {
    const active = isActive(to, exact);
    return (
      <Link
        to={to}
        onClick={isMobile ? onMobileClose : undefined}
        style={{
          display: 'block',
          padding: '8px 20px',
          fontSize: '13.5px',
          textDecoration: 'none',
          color: active ? activeColor : textMuted,
          backgroundColor: active ? activeBg : 'transparent',
          fontWeight: active ? 600 : 400,
          borderLeft: `2px solid ${active ? activeColor : 'transparent'}`,
          transition: 'background 0.12s, color 0.12s',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = hoverBg; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        {label}
      </Link>
    );
  };

  const SectionLabel = ({ label }) => (
    <div style={{
      padding: '14px 20px 6px',
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: textMuted,
      opacity: 0.7,
    }}>
      {label}
    </div>
  );

  const Divider = () => (
    <div style={{ margin: '10px 20px', borderTop: `1px solid ${border}` }} />
  );

  const roleLabel =
    currentUser?.role === 'super_admin' ? 'Super Admin'
    : currentUser?.role === 'admin' ? 'Admin'
    : 'Employee';

  const handleLogout = () => {
    if (isMobile) onMobileClose();
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div
          onClick={onMobileClose}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 199,
          }}
        />
      )}
    <aside style={{
      width: '200px',
      flexShrink: 0,
      borderRight: `1px solid ${border}`,
      backgroundColor: bg,
      display: isMobile ? (mobileOpen ? 'flex' : 'none') : (collapsed ? 'none' : 'flex'),
      flexDirection: 'column',
      ...(isMobile && {
        position: 'fixed',
        top: 0, left: 0, bottom: 0,
        zIndex: 200,
        boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
      }),
    }}>

      {/* Navigation — scrollable */}
      <div style={{ flex: 1, paddingTop: '12px', overflowY: 'auto' }}>
        {!quizRunning && (
          <>
            {(!isRestrictedEmployee || canManageLeads) && (
              <>
                <SectionLabel label="External" />
                {canManageLeads && <NavLink to="/leads" label="Leads" exact />}
                {!isRestrictedEmployee && <NavLink to="/customers" label="Customers" exact />}
                {!isRestrictedEmployee && <NavLink to="/products" label="Products" exact />}
                <Divider />
              </>
            )}

            {canSeeInvoices && (
              <>
                <SectionLabel label="Finances" />
                {!isRestrictedEmployee && <NavLink to="/dashboard" label="Office Expenses" exact />}
                {canSeeInvoices && <NavLink to="/generate-invoice" label={isAdmin ? 'Invoices & Quotations' : 'Invoices'} exact />}
                {/* Receipts hidden for now */}
                <Divider />
              </>
            )}

            <SectionLabel label="Work" />
            <NavLink to="/work/todos"      label="Todos" />
            <NavLink to="/work/timesheets" label="Timesheets" />
            <NavLink to="/work/leave"      label="Calendar" />
            <NavLink to="/work/activity"   label="Activity" />
            {/* Trainings and Reminders hidden for now */}

            <Divider />
            <SectionLabel label="Documentation" />
            <NavLink to="/work/policies" label="Leave Policy" />

            {canManageInterviews && (
              <>
                <Divider />
                <SectionLabel label="HR" />
                {isAdmin && <NavLink to="/people" label="People" />}
                {canManageInterviews && <NavLink to="/hr/interviews" label="Interviews" />}
              </>
            )}
          </>
        )}
      </div>

      {/* User section */}
      <div style={{ borderTop: `1px solid ${border}`, padding: '12px 12px' }}>
        {/* Avatar + name row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(185,28,28,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', fontWeight: 700,
            color: activeColor,
          }}>
            {(currentUser?.firstName?.[0] || '') + (currentUser?.lastName?.[0] || '')}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: textPrimary, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentUser?.loginId ? currentUser.loginId.charAt(0).toUpperCase() + currentUser.loginId.slice(1) : `${currentUser?.firstName} ${currentUser?.lastName}`}
            </div>
            <div style={{ fontSize: '11px', color: textMuted, marginTop: '1px' }}>
              {roleLabel}
            </div>
          </div>
        </div>

        {/* Settings + Logout row */}
        {!quizRunning && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <Link
              to="/profile"
              style={{
                flex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                padding: '6px 8px',
                fontSize: '12px', fontWeight: 500,
                textDecoration: 'none',
                borderRadius: '6px',
                color: isActive('/profile', true) ? activeColor : textMuted,
                background: isActive('/profile', true) ? activeBg : 'rgba(0,0,0,0.04)',
                transition: 'background 0.12s, color 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
              onMouseLeave={e => { e.currentTarget.style.background = isActive('/profile', true) ? activeBg : 'rgba(0,0,0,0.04)'; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
              Settings
            </Link>
            <button
              onClick={handleLogout}
              style={{
                flex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                padding: '6px 8px',
                fontSize: '12px', fontWeight: 500,
                background: 'rgba(0,0,0,0.04)',
                border: 'none',
                borderRadius: '6px',
                color: textMuted,
                cursor: 'pointer',
                transition: 'background 0.12s, color 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.1)'; e.currentTarget.style.color = '#DC2626'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = textMuted; }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
